import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { FormikProps, useFormik } from 'formik'

import { createContext } from '@/utils/create-context'
import { useGetUserData } from '@/hooks/queries/use-get-user-data'
import { useGetRegistrosList } from '@/hooks/queries/use-get-registros'
import { IRegistrosResultado } from '@/interfaces/registros'

interface IRegistrosContextProps {
  formik: FormikProps<IFormProps>
  handleSelectedStatus: (selectedOptions: any) => void
  isActionsUploadOpen: boolean
  isFetchingRegistros: boolean
  isGetRegistrosPendentesList: boolean
  isLoadingRegistros: boolean
  pageSize: number
  registrosList: IRegistrosResultado | undefined
  setIsActionsUploadOpen: Dispatch<SetStateAction<boolean>>
  setIsGetRegistrosPendentesList: Dispatch<SetStateAction<boolean>>
  setPage: Dispatch<SetStateAction<number>>
  setPageSize: Dispatch<SetStateAction<number>>
  setSearch: Dispatch<SetStateAction<string>>
  totalItens: number
  totalPages: number
  userData?: any
}
interface IFormProps {
  campo?: string
  comprimento?: number
  dataFim?: string
  dataInicio?: string
  direcao?: string
  idEmpregador?: string
  inicio?: number
  pageSize?: number
  procurar?: string
  search?: string
  situacoes?: string
}

const [useRegistrosContext, RegistrosProvider] = createContext<IRegistrosContextProps>()

const RegistrosContext = ({ children }: { children: React.ReactNode }) => {
  const { data: userData } = useGetUserData()
  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(10)
  const [search, setSearch] = useState('')
  const [filterValues, setFilterValues] = useState<IFormProps>({} as IFormProps)
  const [isActionsUploadOpen, setIsActionsUploadOpen] = useState(false)
  const [isGetRegistrosPendentesList, setIsGetRegistrosPendentesList] = useState<boolean>(false)
  const inicio = (page - 1) * pageSize

  useEffect(() => {
    page > 1 && setPage(1)
  }, [pageSize])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      dataInicio: new Date(new Date().setMonth(new Date().getMonth() - 1))
        .toISOString()
        .split('T')[0],
      dataFim: new Date(new Date().setDate(new Date().getDate() + 1))
        .toISOString()
        .split('T')[0],
      campo: 'importadoEm',
      comprimento: pageSize,
      inicio: 0,
      direcao: 'desc',
      idEmpregador: userData?.authenticatedAccountId ?? '',
      procurar: search,
    } as IFormProps,
    onSubmit: (values) => {
      const { situacoes, ...rest } = values
      setFilterValues({
        ...rest,
        ...(situacoes && situacoes !== 'todos' ? { situacoes } : {}),
      })
    }
  })


  const {
    data: registrosList,
    isLoading: isLoadingRegistros,
    isFetching: isFetchingRegistros,
  } = useGetRegistrosList({
    params: {
      ...filterValues,
      inicio,
      comprimento: pageSize,
      procurar: search,
      idEmpregador: userData?.authenticatedAccountId ?? '',
      campo: 'importadoEm',
      direcao: 'desc',
      dataInicio: formik?.values?.dataInicio + 'T00:00:00',
      dataFim: formik?.values?.dataFim + 'T23:59:59'
    }
  })
  const totalPages = Math.ceil(
    (registrosList?.conteudo?.totalDeRegistros ?? 0) / pageSize
  )

  const totalItens = registrosList?.conteudo?.registros?.length ?? 0

  const handleSelectedStatus = (selectedOption: any) => {
    if (selectedOption?.some((option: any) => option.value == 'todos')) {
      formik.setFieldValue('situacoes', selectedOption)
    }
  }

  const contextValue = useMemo(() => ({
    formik,
    handleSelectedStatus,
    isActionsUploadOpen,
    isFetchingRegistros,
    isGetRegistrosPendentesList,
    isLoadingRegistros,
    pageSize,
    registrosList,
    setIsActionsUploadOpen,
    setIsGetRegistrosPendentesList,
    setPage,
    setPageSize,
    setSearch,
    totalItens,
    totalPages,
    userData,
  }), [
    formik,
    handleSelectedStatus,
    isActionsUploadOpen,
    isFetchingRegistros,
    isGetRegistrosPendentesList,
    isLoadingRegistros,
    pageSize,
    registrosList,
    setIsActionsUploadOpen,
    setIsGetRegistrosPendentesList,
    setPage,
    setPageSize,
    setSearch,
    totalItens,
    totalPages,
    userData,
  ])

  return (
    <RegistrosProvider value={contextValue}>
      {children}
    </RegistrosProvider>
  )
}

export { RegistrosContext, useRegistrosContext }
